import {createApi} from '@reduxjs/toolkit/query/react';
import {AxiosRequestHeaders} from 'axios';

import httpBaseQuery from '../../../shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from '../../../shared/config/model/urls';
import IServerOutput, {IServerStatusOutput} from '../model/Server/IServer';
import CreateServerType from '../model/Server/CreateServerType';
import UpdateServerType from '../model/Server/UpdateServerType';
import {IServerMetricsParams, IServerMetricsSeries, IServerMount} from '../model/Server/IServerMetrics';
import IHydraResponse from '../../../shared/api/model/HydraResponse';
import IPagination from '../../../shared/api/model/IPagination';
import {paginationAdapter} from '../../../shared/api/paginationHelper';
import {providesListUuid} from '../../../shared/api/rtk-query/RTKQueryHelper';
import {buildQuery} from '../../../shared/api/query';
import ICollectionParams from '../../../shared/api/model/ICollectionParams';
import IOrderBy from '../../../shared/model/IOrderBy';

export const serverAPI = createApi({
    reducerPath: 'serverAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['Server'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        addServer: build.mutation<IServerOutput, CreateServerType>({
            query: (data) => ({
                path: ApiUrls.SERVERS(),
                method: 'POST',
                data,
            }),
            invalidatesTags: [{ type: 'Server', id: 'LIST' }],
        }),
        getServers: build.query<IPagination<IServerOutput>, ICollectionParams>({
            query: ({page, sort, filters}) => ({
                path: buildQuery(ApiUrls.SERVERS(page, sort, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesListUuid(result?.items, 'Server'),
            transformResponse(
                rawResult: IHydraResponse<IServerOutput>,
            ): Promise<IPagination<IServerOutput>> | IPagination<IServerOutput> {
                return paginationAdapter(rawResult);
            },
        }),
        getAllServerIds: build.query<string[], { sort?: IOrderBy; filters?: string[] }>({
            query: ({sort, filters}) => ({
                path: ApiUrls.SERVERS_IDS(sort, filters),
                headers: {Accept: 'application/json'} as unknown as AxiosRequestHeaders,
            }),
        }),
        getServer: build.query<IServerOutput, string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER(uuid),
            }),
            providesTags: (result, error, uuid) => [{ type: 'Server', id: uuid }],
        }),
        updateServer: build.mutation<IServerOutput, UpdateServerType>({
            query: ({uuid, ...data}) => ({
                path: ApiUrls.SERVER(uuid),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ uuid, ...patch }, { dispatch }) {
                dispatch(
                    serverAPI.util.updateQueryData('getServer', uuid, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: (result, error, {uuid}) => [{ type: 'Server', id: uuid }, { type: 'Server', id: 'LIST' }],
        }),
        deleteServer: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER(uuid),
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'Server', id: 'LIST' }],
        }),
        pauseServer: build.mutation<IServerOutput, string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER_PAUSE(uuid),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, uuid) => [{ type: 'Server', id: uuid }, { type: 'Server', id: 'LIST' }],
        }),
        resumeServer: build.mutation<IServerOutput, string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER_RESUME(uuid),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, uuid) => [{ type: 'Server', id: uuid }, { type: 'Server', id: 'LIST' }],
        }),
        regenerateServerToken: build.mutation<IServerOutput, string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER_REGENERATE_TOKEN(uuid),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, uuid) => [{ type: 'Server', id: uuid }],
        }),
        getServerMetrics: build.query<IServerMetricsSeries, IServerMetricsParams>({
            query: ({uuid, metrics, mounts, from, to, step}) => {
                const params: Record<string, string | number | string[]> = {
                    'metrics[]': metrics,
                    ...(mounts && mounts.length > 0 && {'mounts[]': mounts}),
                    ...(from !== undefined && {from}),
                    ...(to !== undefined && {to}),
                    ...(step !== undefined && {step}),
                };

                return {
                    path: buildQuery(ApiUrls.SERVER_METRICS(uuid), params),
                };
            },
        }),
        getServerMounts: build.query<IServerMount[], string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER_MOUNTS(uuid),
            }),
        }),
        getServerStatus: build.query<IServerStatusOutput, string>({
            query: (uuid) => ({
                path: ApiUrls.SERVER_STATUS(uuid),
            }),
        }),
    }),
});
